<template>
  <div class="container rtb-container" v-if="auction.id">

    <div class="auction-info-container">
      <div class="auction-info">
        <div class="photo" :style="'background-image: url(' + auctionPhoto() + ')'">
          <div style="position: absolute; top: .5rem; left: .5rem; width: 2rem;"><img :src="tenantLogoUrl(tenantProfile)" style="width: 100%"/></div>
        </div>
        <div class="text">
          <strong class="name">{{ auction.address }}</strong><br/>
          {{ auction.zip }} {{ auction.city }}, {{ auction.state }}<br/>
          APN: {{ auction.metadata.apn || '—' }}<br/>
          <strong class="sub">Auction Period:</strong><br/>
          {{ auction.startDate | prettyDateTime(auction.metadata.timezone) }} &ndash;<br/>
          {{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}<br/>
        </div>
      </div>
      <div class="questions">
        <AgentCard :userId="auction.listingAgent" :tenantSubdomain="auction.tenant.subdomain" />
      </div>
    </div>

    <!-- verify that auction status is open -->
    <template v-if="auction.status !== auctionStatuses.OPEN.value">
      <div class="rtb">
        <h1 style="padding-bottom: 0;">Register to Bid</h1>
        <p>This auction is not open to new registrations. Either the auction is already ongoing or in closed/processing winner or escrow process.</p>
        <router-link :to="'/auction/' + auction.id"><button>Go to auction page</button></router-link>
      </div>
    </template>

    <!-- verify that user is a buyer that has registered to the auction -->
    <template v-else-if="auction.role.role === auctionRoles.ANONYMOUS.value">
      <div class="rtb">
        <h1 style="padding-bottom: 0;">Register to Bid</h1>
        <p>You are not yet a registered applicant. Please click the 'Register to bid' button on the auction page.</p>
        <router-link :to="'/auction/' + auction.id"><button>Go to auction page</button></router-link>
      </div>
    </template>

    <!-- ok, show to register to bid flow -->
    <template v-else-if="auction.role.role === auctionRoles.ENTRY.value || auction.role.role === auctionRoles.APPLICANT.value">

      <div class="rtb" v-if="step === 'sent'">
        <h1>Registration form sent!</h1>
        <div class="p">
          You have completed your auction registration. The agent responsible for this auction will review your
          registration and notify you regarding the progress as soon as possible. You will receive a notification
          email on any changes. You can also check the status anytime from your <router-link to="/dashboard">Dashboard</router-link>.
        </div>
        <AuctionBrokerInfoWidget type="message" :initialAuction="auction" />
        <div class="align-center">
          <router-link :to="'/auction/' + auction.id"><button class="wider">Back to the auction page</button></router-link>
        </div>
      </div>

      <div class="rtb" v-else>
        <h1>Register to Bid ({{ numberOfPassedSteps() }}/{{ numberOfSteps() }})</h1>

        <div class="grey-box-container container">
          <div class="top-bar" @click="step = 'P'">
            <h2>Personal Information</h2>
            <div class="status" v-if="isSavedAndValid('P')">
              <v-icon style="font-size:2em" color="pfxBrand">check_circle</v-icon>
            </div>
          </div>
          <div v-if="step === 'P'">
            <form ref="personalInfoForm">
              <div class="align-left">
                <fieldset>
                  <label>Username * <small>&mdash; this is your display name in the auction</small></label>
                  <input type="text" class="readonly" readonly :value="$store.state.user.profile.alias" required />
                </fieldset>
                <fieldset>
                  <label>Real Name * <small>&mdash; visible only to brokers</small></label>
                  <input type="text" class="readonly" readonly :value="$store.state.user.profile.firstName + ' ' + $store.state.user.profile.lastName" />
                </fieldset>
                <fieldset>
                  <label>Cell Phone Number *</label>
                  <input type="tel" class="readonly" readonly :value="$store.state.user.profile.phone" />
                </fieldset>
              </div>
              <div class="p" style="margin: 0 0 1em;">
                <v-icon color="grey">chevron_right</v-icon> Make sure the above details are correct, and if you need to make changes,
                you can do it from your Profile Page.
              </div>
              <div class="align-left">
                <fieldset>
                  <label for="buyerEntity">Name of Entity <small>&mdash; (optional) if an entity is purchasing the property</small></label>
                  <input type="text" v-model.trim="buyerEntity" id="buyerEntity"/>
                </fieldset>
              </div>
              <div class="align-right">
                <button class="btn-invisible" @click.prevent="nextStep('A', false)">Skip for now</button>
                <button class="btn-neutral-light wider" @click.prevent="nextStep('A', true)">Save</button>
              </div>
            </form>
          </div>
        </div>

        <div class="grey-box-container container">
          <div class="top-bar" @click="step = 'A'">
            <h2>Agency Relationship</h2>
            <div class="status" v-if="isSavedAndValid('A')">
              <v-icon color="pfxBrand" style="font-size:2em">check_circle</v-icon>
            </div>
          </div>
          <div v-if="step === 'A'">
            <div class="p align-left">

              <v-radio-group v-model="agencyRelationship">
                <v-radio color="pfxBrand" :value="agencyRelationships.LISTING_AGENT.value" :label="agencyRelationships.LISTING_AGENT.label" />
                <v-radio color="pfxBrand" :value="agencyRelationships.MYSELF.value" :label="agencyRelationships.MYSELF.label" />
                <v-radio color="pfxBrand" :value="agencyRelationships.SELLING_AGENT.value" :label="agencyRelationships.SELLING_AGENT.label" />
              </v-radio-group>

              <div v-if="agencyRelationship === agencyRelationships.SELLING_AGENT.value">
                <form ref="agentContactInfo">
                  <fieldset>
                    <label for="sellingAgentEmail">Agent email address *</label>
                    <input type="email" v-model.trim="sellingAgentEmail" id="sellingAgentEmail" required />
                  </fieldset>
                  <small>
                    An invitation email will be sent to the above email address, and the receiver needs to
                    follow the instructions in the email to accept and join your auction as a selling agent.
                  </small>
                </form>
              </div>

            </div>
            <div class="align-right">
              <button class="btn-invisible" @click.prevent="nextStep('O', false)">Skip for now</button>
              <button class="btn-neutral-light wider" @click="nextStep('O', true)">Save</button>
            </div>
          </div>
        </div>

        <div class="grey-box-container container">
          <div class="top-bar" @click="step = 'O'">
            <h2>Observers <small>(Optional)</small></h2>
            <div class="status" v-if="isSavedAndValid('O')">
              <v-icon color="pfxBrand" style="font-size:2em">check_circle</v-icon>
            </div>
          </div>
          <div v-if="step === 'O'">
            <div class="p align-left">
              <p>You can add observers only to view your auction process.</p>
              <div class="p" v-if="invitationsObservers.length > 0">
                Observers currently invited:
                <ul class="list-bullets" style="font-weight: 600">
                  <li v-for="email in invitationsObservers" :key="email">{{ email }}</li>
                </ul>
              </div>
              <form ref="inviteObserverForm">
                <fieldset>
                  <label>Observer Email Addresses</label>
                  <input type="email" v-for="(email, index) in inviteObserverEmails" :key="index" v-model.trim="inviteObserverEmails[index]" placeholder="Email address"/>
                </fieldset>
              </form>

            </div>
            <div class="align-right">
              <button class="btn-invisible" @click.prevent="nextStep('R', false)">Skip for now</button>
              <button class="btn-neutral-light wider" @click="nextStep('R', true)">Save</button>
            </div>
          </div>
        </div>

        <div class="grey-box-container container">
          <div class="top-bar" @click="step = 'R'">
            <h2>Required Documents</h2>
            <div class="status" v-if="isSavedAndValid('R')">
              <v-icon color="pfxBrand" style="font-size:2em">check_circle</v-icon>
            </div>
          </div>
          <div v-if="step === 'R'">
            <div class="p list-bullets align-left">
              <vue-markdown :source="auction.metadata.instructions" />
              <ul class="list-bullets">
                <li v-for="req in auction.buyerRequirements" :key="req.docType">{{ getDocLabel(req.docType) }}</li>
              </ul>
              <p><small>All documents uploaded to PFX are completely confidential and protected in our document vault for this auction period.</small></p>
            </div>
            <div class="clearfix row">
              <FileBox :auctionId="auction.id" usecase="buyerdocs" />
            </div>
            <div class="align-right">
              <button class="btn-invisible" @click.prevent="nextStep('T', false)">Skip for now</button>
              <button class="btn-neutral-light wider" @click="nextStep('T', true)">Save</button>
            </div>
          </div>
        </div>

        <div class="grey-box-container container">
          <div class="top-bar" @click="step = 'T'">
            <h2>Terms and Agreements</h2>
            <div class="status" v-if="isSavedAndValid('T')">
              <v-icon color="pfxBrand" style="font-size:2em">check_circle</v-icon>
            </div>
          </div>
          <div v-if="step === 'T'">
            <div class="p align-left" style="padding-bottom: 1rem;">
              <form ref="termsAndAgreementsForm">
                <fieldset>
                  <p>
                    By agreeing to the Terms and Agreements, you agree that you have reviewed the terms in the
                    purchase agreement and the documents in the Document Vault and understand that there is
                    no due diligence period after winning the auction.
                  </p>
                  <label for="terms">
                    <input id="terms" type="checkbox" v-model="termsChecked" required />
                    I have read and agree to the <a @click.prevent="$store.dispatch('openTermsAndConditionsPopup')">Terms of Service</a>,
                    <a @click.prevent="$store.dispatch('openBrokerRegistrationPopup')">Broker Registration Terms</a> and the 
                    <a @click.prevent="$store.dispatch('openTermsAndConditionsPopup')">Privacy Policy</a> of PFX Real Estate Auction Platform.
                  </label>
                </fieldset>
              </form>
            </div>
            <div class="align-right">
              <button class="btn-invisible" @click.prevent="nextStep('', false)">Skip for now</button>
              <button class="btn-neutral-light wider" @click="nextStep('', true)">Save</button>
            </div>
          </div>
        </div>

        <div class="clearfix align-right">
          <router-link :to="'/auction/' + auction.id"><button class="btn-invisible">Cancel</button></router-link>
          <button style="margin-left: 1rem;" class="wider" @click="nextStep('submit', false)" :disabled="submitDisabled()">Submit</button>
        </div>

      </div>

    </template>

  </div>
</template>

<script>
  import AuctionBrokerInfoWidget from './AuctionBrokerInfoWidget.vue'
  import AgentCard from './elements/AgentCard.vue'
  import { auctionStatuses, auctionRoles, agencyRelationships, auctionInvitationRoles, tenantLogoUrl, fileDocTypeLabel } from '../utils/PfxUtils.js'
  import FileBox from './elements/FileBox.vue'
  import FileList from './elements/FileList.vue'
  import VueMarkdown from 'vue-markdown'

  export default {
    name: 'RegisterToBidPage',
    components: {
      AuctionBrokerInfoWidget,
      AgentCard,
      FileBox,
      FileList,
      VueMarkdown
    },
    data() {
      return {
        auctionStatuses,
        auctionRoles,
        agencyRelationships,
        fileDocTypeLabel,
        auctionId: this.$route.params.auctionId,
        auction: { metadata: {} },
        tenantProfile: {},
        step: 'P',
        buyerEntity: '',
        agencyRelationship: '',
        sellingAgentEmail: '',
        initialSellingAgentEmail: '',
        inviteObserverEmails: ['', ''],
        termsChecked: false,
        invitationsObservers: [],
        buyerRequirementOptions: []
      }
    },
    watch: {
      inviteObserverEmails() {
        const numberOfEmptyFields = this.inviteObserverEmails.filter(e => e.length === 0).length
        if (numberOfEmptyFields < 2) { this.inviteObserverEmails.push('') }
        if (numberOfEmptyFields > 3) { this.inviteObserverEmails.splice(this.inviteObserverEmails.findIndex(e => e.length === 0), 1) }
      }
    },
    created() {
      this.$PfxBackendApi.getBuyerRequirementsOptions().then(res => {
          // console.log('getBuyerRequirementsOptions res', res)
          res.data.forEach(document => {
            let item = { text: document.name, id: document.id }
            this.buyerRequirementOptions.push(item)
          })
          console.log('getBuyerRequirementsOptions Array: ', this.buyerRequirementOptions)
        }).catch(err => {
          console.error('getBuyerRequirementsOptions err', err)
        })
    },
    mounted() {
      this.$store.state.loadingSpinner = true
      this.$PfxBackendApi.getAuction(this.auctionId).then(res => {
        console.log('getAuction res', res)
        this.auction = res.data || {}
        this.buyerEntity = this.auction.role.buyerEntity
        this.agencyRelationship = this.auction.role.agencyRelationship
        this.initialSellingAgentEmail = this.auction.role.sellingAgentEmail
        this.sellingAgentEmail = this.auction.role.sellingAgentEmail
        this.$store.state.loadingSpinner = 1

        // resolve which step should be displayed (first one that has not been saved) [note that steps can be saved out of order]
        if (this.isSavedAndValid('P')) {
          this.step = 'A'
          if (this.isSavedAndValid('A')) {
            this.step = 'O'
            if (this.isSavedAndValid('O')) {
              this.step = 'R'
              if (this.isSavedAndValid('R')) {
                this.step = 'T'
                if (this.isSavedAndValid('T')) {
                  this.step = ''
                }
              }
            }
          }
        }
        if (this.isSavedAndValid('T')) { this.termsChecked = true } // a bit kludge: termsChecked is practically same thing as "terms step was saved"

        this.$PfxBackendApi.getAuctionInvitationsByMe(this.auction.tenant.subdomain, this.auction.id).then(res => {
          console.log('getAuctionInvitationsByMe res', res)
          const invitations = res.data || []
          invitations.forEach(invitation => {
            console.log('invited [%s] [%s]', invitation.email, invitation.auctionInvitationRole)
            if (invitation.auctionInvitationRole === auctionInvitationRoles.SELLING_AGENT.value) {
              if (!this.initialSellingAgentEmail) {
                this.initialSellingAgentEmail = invitation.email
                this.sellingAgentEmail = invitation.email
              }
            } else if (invitation.auctionInvitationRole === auctionInvitationRoles.BUYER_OBSERVER.value) {
              this.invitationsObservers.push(invitation.email)
            }
          })
        }).catch(err => {
          console.error('getAuctionInvitationsByMe err', err)
        })

        // fetch tenant profile (for logo)
        this.$PfxBackendApi.getTenantPublic(this.auction.tenant.subdomain).then(res => {
          console.log('getTenantPublic res', res)
          this.tenantProfile = res.data || {}
        }).catch(err => {
          console.error('getTenantPublic err', err)
        }).then(() => this.$store.state.loadingSpinner--)

      }).catch(err => {
        console.error('getAuction err', err)
        this.$notify({ type: 'danger', message: 'Cannot fetch auction data' })
        this.$store.state.loadingSpinner = false
      })
    },
    methods: {
      tenantLogoUrl,

      auctionPhoto() {
        if (this.auction.metadata && this.auction.metadata.photos && this.auction.metadata.photos.length > 0) {
          return this.auction.metadata.photos[0]
        } else {
          return require('../assets/placeholder-photos.jpg')
        }
      },

      nextStep(nextStep, validateAndSave) {
        if (validateAndSave) {
          if (!this.validate(this.step)) {
            return // validation error happened
          } else {
            this.auction.role.registerToBidFormStatus = this.auction.role.registerToBidFormStatus || ''
            if (this.auction.role.registerToBidFormStatus.indexOf(this.step) === -1) {
              this.auction.role.registerToBidFormStatus += this.step
            }
            this.saveApplication()
          }
        }
        if (nextStep === 'submit') {
          this.registerToBid()
        }
        this.step = nextStep
      },

      validate(step) {
        if (step === 'P') {
          if (!this.$store.state.user.profile.alias ||
            !this.$store.state.user.profile.firstName ||
            !this.$store.state.user.profile.lastName ||
            !this.$store.state.user.profile.phone) {
            this.$store.commit('openDialog', 'Profile not complete, please update it.')
            return false
          }

        } else if (step === 'A') {
          if (!this.agencyRelationship) {
            this.$store.commit('openDialog', 'Select the agency relationship.')
            return false
          }
          if (this.agencyRelationship === agencyRelationships.SELLING_AGENT.value) {
            if (this.$refs.agentContactInfo && !this.$refs.agentContactInfo.checkValidity()) {
              this.$refs.agentContactInfo.reportValidity()
              return false
            }
          }
          if (this.agencyRelationship === agencyRelationships.MYSELF.value) {
            if (!this.$store.state.user.profile.dreNumber) {
              this.$store.commit('openDialog', 'Your profile does not contain a DRE license number, please update it.')
              return false
            }
          }

        } else if (step === 'O') {
          if (this.inviteObserverEmails.length > 0) {
            const atLeastOneEmailFilled = this.inviteObserverEmails.filter(email => email.length > 0).length > 0
            if (atLeastOneEmailFilled) {
              if (this.$refs.inviteObserverForm && !this.$refs.inviteObserverForm.checkValidity()) {
                this.$refs.inviteObserverForm.reportValidity()
                return false
              }
            }
          }

        } else if (step === 'T') {
          if (this.$refs.termsAndAgreementsForm && !this.$refs.termsAndAgreementsForm.checkValidity()) {
            this.$refs.termsAndAgreementsForm.reportValidity()
            return false
          }
        }
        return true
      },

      isSavedAndValid(step) {
        const status = this.auction.role.registerToBidFormStatus || ''
        return status.indexOf(step) > -1
      },

      submitDisabled() {
        if (!this.isSavedAndValid('P')) return true
        if (!this.isSavedAndValid('A')) return true
        if (!this.isSavedAndValid('R')) return true
        if (!this.isSavedAndValid('T')) return true
        // all steps valid
        return false
      },

      numberOfPassedSteps() {
        let count = 0
        if (this.isSavedAndValid('P')) { count++ }
        if (this.isSavedAndValid('A')) { count++ }
        if (this.isSavedAndValid('O')) { count++ }
        if (this.isSavedAndValid('R')) { count++ }
        if (this.isSavedAndValid('T')) { count++ }
        return count
      },
      numberOfSteps() {
        return 5
      },

      saveApplication() {
        const that = this
        const data = {
          buyerEntity: this.buyerEntity || '',
          agencyRelationship: this.agencyRelationship,
          sellingAgentEmail: this.sellingAgentEmail,
          registerToBidFormStatus: this.auction.role.registerToBidFormStatus
        }

        that.$store.state.loadingSpinner = true
        that.$PfxBackendApi.postAuctionApplicationSave(that.auction.tenant.subdomain, that.auction.id, data).then(res => {
          console.log('postAuctionApplicationSave res', res)
          if (res.data && res.data.id) {
            that.auction.role = res.data
          }
        }).catch(err => {
          console.error('postAuctionApplicationSave error', err)
          this.$notify({ type: 'danger', message: 'Unexpected error during saving the registration: ' + err.message })
        }).then(() => that.$store.state.loadingSpinner = false)
      },

      registerToBid() {
        const that = this
        const data = {
          buyerEntity: this.buyerEntity || '',
          agencyRelationship: this.agencyRelationship,
          sellingAgentEmail: this.sellingAgentEmail,
          registerToBidFormStatus: this.auction.role.registerToBidFormStatus
        }

        that.$store.state.loadingSpinner = 1
        that.$PfxBackendApi.postAuctionApplicationSubmit(that.auction.tenant.subdomain, that.auction.id, data).then(res => {
          console.log('postAuctionApplicationSubmit res', res) // res.data = { id: 6, role: 'APPLICANT', userId: 'xxxxxxxx-xxxx-xxxx-xxxxxxxxxxxx' }
          if (res.data && res.data.id && res.data.role === auctionRoles.APPLICANT.value) {
            that.auction.role = res.data
            that.$store.state.loadingSpinner--

            const sendSellingAgentInvitation =
              that.agencyRelationship === auctionRoles.SELLING_AGENT.value &&
              that.sellingAgentEmail !== that.initialSellingAgentEmail // selling agent email address was changed
            if (sendSellingAgentInvitation) {
              that.$store.state.loadingSpinner++
              that.$PfxBackendApi.createAuctionInvitation(that.auction.tenant.subdomain, that.auction.id, that.auction.role.id, auctionInvitationRoles.SELLING_AGENT.value, that.sellingAgentEmail).then(res => {
                console.log('createAuctionInvitation (SELLING_AGENT) res', res)
                this.$notify({ type: 'info', message: 'Invitation sent to ' + that.sellingAgentEmail })
              }).catch(err => {
                console.error('createAuctionInvitation (SELLING_AGENT) error', err)
                this.$notify({ type: 'danger', message: 'Cannot send the agent invitation email' })
              }).then(() => {
                that.$store.state.loadingSpinner--
              })
            }

            const sendBuyerObserverInvitation = that.inviteObserverEmails.filter(email => email.length > 0).length > 0
            if (sendBuyerObserverInvitation) {
              // Using Set to filter duplicated emails then spread it into an array
              [...new Set(that.inviteObserverEmails.filter(email => email.length > 0))].forEach(email => {
                that.$store.state.loadingSpinner++
                that.$PfxBackendApi.createAuctionInvitation(that.auction.tenant.subdomain, that.auction.id, that.auction.role.id, auctionInvitationRoles.BUYER_OBSERVER.value, email).then(res => {
                  console.log('createAuctionInvitation (BUYER_OBSERVER) res', res)
                  this.$notify({type: 'info', message: 'Invitation sent to ' + email})
                }).catch(err => {
                  console.error('createAuctionInvitation (BUYER_OBSERVER) error', err)
                  this.$notify({type: 'danger', message: 'Cannot send the observer invitation email'})
                }).then(() => {
                  that.$store.state.loadingSpinner--
                })
              })
            }

          }
          that.step = 'sent'
        }).catch(err => {
          console.error('postAuctionApplicationSubmit error', err)
          this.$notify({ type: 'danger', message: 'Unexpected error during sending the registration: ' + err.message })
          that.$store.state.loadingSpinner = false
        })
      },
      getDocLabel(id){
        return fileDocTypeLabel(id, this.buyerRequirementOptions)
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .grey-box-container {
    background-color: $grey-box-bg;
    border-radius: 14px;
    box-shadow: 0 2px 4px 0 rgba(189, 189, 189, 0.5);
    margin-bottom: 1rem;
    padding: 1rem 1.5rem;
    width: 800px;

    h2 { margin-top: 0; }
  }

  .auction-info-container {
    position: relative;
    @include clearfix();

    .auction-info {
      position: relative;
      @include stack();
      @include tablet-landscape-breakpoint {
        @include column(2/3);
      }

      .photo {
        width: 200px;
        height: 150px;
        background-size: cover;
        background-position: center;
        background-color: #aad2e3;
        float: left;
      }
      .text {
        margin-left: calc(200px + 1rem)
      }
    }

    .questions {
      @include stack();
      @include tablet-landscape-breakpoint {
        @include column(1/3);
        text-align: right;
      }
    }
  }

  .auction-info-container {
    padding: 1rem 0;
    border-bottom: 1px solid #f0f0f0;

    .auction-info {
      .text {
        .name {
          font-size: 1.25em;
          line-height: 1em;
        }
        .sub {
          display: inline-block;
          padding-top: 0.5em;
        }
      }
    }
  }

  .rtb-container {
    padding-bottom: 5rem;
  }

  .rtb {
    @include center($max-width: 42rem);

    h1 {
      font-family: 'Encode Sans', sans-serif;
      font-weight: 300;
      font-size: 1.6rem;
      text-align: left;
      padding: 2rem 0 2rem;
    }
    h2 {
      font-weight: 600;
      font-size: 1.2rem;
      small {
        color: #aaa;
      }
    }
    label {
      font-weight: 600;
    }

    .stepper.no-shadow {
      box-shadow: none;
    }

    .container {
      .top-bar {
        @include clearfix();
        cursor: pointer;

        h2 {
          @include column(7/8);
        }
        .status {
          @include column(1/8);
          text-align: right;
          margin-top: 0.5em;
        }
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/RegisterToBidPage.vue